import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, DollarSign, Clock, Percent, AlertTriangle, 
  Save, RefreshCw, Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPricingSettings({ token }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/pricing-config/v2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.patch(
        `${API_URL}/api/admin/pricing-config/v2`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            platform_fee_percent: config.platform_fee_percent,
            credit_window_days: config.credit_window_days,
            credit_enabled: config.credit_enabled,
            over_range_threshold_percent: config.over_range_threshold_percent,
            diagnostic_payout_reduced_percent: config.diagnostic_payout_reduced_percent,
            fixed_scope_enabled: config.fixed_scope_enabled
          }
        }
      );
      setConfig(response.data);
      setHasChanges(false);
      toast.success('Pricing configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Pricing Model v2 Settings</h2>
            <p className="text-sm text-neutral-500">Configure diagnostic fee and credit parameters</p>
          </div>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges || saving}
          className="bg-black hover:bg-neutral-800"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Settings Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Platform Fee */}
        <div className="bg-white rounded-xl p-6 border border-neutral-200">
          <div className="flex items-center gap-2 mb-4">
            <Percent className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold">Platform Fee</h3>
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            Percentage retained from the diagnostic fee. This is ZOLT&apos;s only revenue from each job.
          </p>
          <div className="space-y-2">
            <Label htmlFor="platform_fee">Platform Fee (%)</Label>
            <Input
              id="platform_fee"
              type="number"
              step="0.5"
              min="0"
              max="20"
              value={config.platform_fee_percent}
              onChange={(e) => handleChange('platform_fee_percent', parseFloat(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-neutral-400">Recommended: 6%</p>
          </div>
        </div>

        {/* Credit Window */}
        <div className="bg-white rounded-xl p-6 border border-neutral-200">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold">Credit Window</h3>
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            How long customers have to use their diagnostic fee credit toward the final bill.
          </p>
          <div className="space-y-2">
            <Label htmlFor="credit_window">Credit Window (days)</Label>
            <Input
              id="credit_window"
              type="number"
              min="1"
              max="90"
              value={config.credit_window_days}
              onChange={(e) => handleChange('credit_window_days', parseInt(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-neutral-400">Recommended: 30 days</p>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div>
              <Label htmlFor="credit_enabled">Credit Feature Enabled</Label>
              <p className="text-xs text-neutral-400">Allow diagnostic fee to be credited</p>
            </div>
            <Switch
              id="credit_enabled"
              checked={config.credit_enabled}
              onCheckedChange={(checked) => handleChange('credit_enabled', checked)}
            />
          </div>
        </div>

        {/* Over-Range Threshold */}
        <div className="bg-white rounded-xl p-6 border border-neutral-200">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold">Over-Range Protection</h3>
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            If contractor quote exceeds the estimate range by this %, they must provide justification and notes.
          </p>
          <div className="space-y-2">
            <Label htmlFor="over_range_threshold">Threshold (%)</Label>
            <Input
              id="over_range_threshold"
              type="number"
              step="5"
              min="5"
              max="100"
              value={config.over_range_threshold_percent}
              onChange={(e) => handleChange('over_range_threshold_percent', parseFloat(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-neutral-400">Recommended: 25%</p>
          </div>
        </div>

        {/* Reduced Payout */}
        <div className="bg-white rounded-xl p-6 border border-neutral-200">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold">Anti-Abuse Payout</h3>
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            If customer declines an over-range quote, contractor receives this % of their diagnostic pool.
          </p>
          <div className="space-y-2">
            <Label htmlFor="reduced_payout">Reduced Payout (%)</Label>
            <Input
              id="reduced_payout"
              type="number"
              step="5"
              min="0"
              max="100"
              value={config.diagnostic_payout_reduced_percent}
              onChange={(e) => handleChange('diagnostic_payout_reduced_percent', parseFloat(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-neutral-400">
              Recommended: 60% (contractor gets 60% of diagnostic pool if over-range quote declined)
            </p>
          </div>
        </div>

        {/* Fixed-Scope Jobs */}
        <div className="bg-white rounded-xl p-6 border border-neutral-200 md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold">Fixed-Scope Jobs</h3>
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            Enable predefined jobs with fixed pricing (e.g., Boiler Service £89, Smoke Alarm Install £45).
          </p>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="fixed_scope_enabled">Fixed-Scope Jobs Enabled</Label>
              <p className="text-xs text-neutral-400">Coming soon - catalogue of fixed-price services</p>
            </div>
            <Switch
              id="fixed_scope_enabled"
              checked={config.fixed_scope_enabled}
              onCheckedChange={(checked) => handleChange('fixed_scope_enabled', checked)}
            />
          </div>
        </div>
      </div>

      {/* Example Calculation */}
      <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
        <h3 className="font-semibold mb-4">Example Calculation</h3>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4">
            <p className="text-neutral-500 mb-1">Diagnostic Fee</p>
            <p className="text-xl font-bold">£60.00</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-neutral-500 mb-1">Platform Fee ({config.platform_fee_percent}%)</p>
            <p className="text-xl font-bold text-amber-600">£{(60 * config.platform_fee_percent / 100).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-neutral-500 mb-1">Contractor Pool</p>
            <p className="text-xl font-bold text-green-600">£{(60 * (1 - config.platform_fee_percent / 100)).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-neutral-500 mb-1">Creditable to Customer</p>
            <p className="text-xl font-bold text-blue-600">£{(60 * (1 - config.platform_fee_percent / 100)).toFixed(2)}</p>
          </div>
        </div>
        <p className="text-xs text-neutral-400 mt-4">
          * If quote is over-range by &gt;{config.over_range_threshold_percent}% and declined, 
          contractor receives {config.diagnostic_payout_reduced_percent}% of pool = 
          £{(60 * (1 - config.platform_fee_percent / 100) * config.diagnostic_payout_reduced_percent / 100).toFixed(2)}
        </p>
      </div>
    </div>
  );
}
